import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { supabase, User, Tables, RecurringTask } from "../utils/supabase";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  CalendarDaysIcon,
  UserIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon,
  ScaleIcon,
  TrashIcon,
  EyeIcon,
  ClipboardDocumentListIcon,
  ArrowPathIcon,
  PauseIcon,
  PlayIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  ChevronDownIcon,
  ForwardIcon,
  ViewColumnsIcon,
  ListBulletIcon,
} from "@heroicons/react/24/outline";
import KanbanBoard from "../components/kanban/KanbanBoard";
import KanbanFilters, { KanbanFilterState } from "../components/kanban/KanbanFilters";
import { format, isToday, isTomorrow, isPast, parseISO, addDays, addWeeks, addMonths, addYears, differenceInDays } from "date-fns";
import { nl } from "date-fns/locale";
import toast from "react-hot-toast";
import { exportToCsv } from "../utils/exportCsv";
import Pagination from "../components/Pagination";
import { logAuditEvent } from "../utils/auditLog";

const TASKS_PAGE_SIZE = 25;

interface TaskWithUser {
  id: string;
  title: string;
  description: string;
  type:
    | "communication"
    | "calibration"
    | "expiry_check"
    | "complaint"
    | "orders"
    | "other";
  status: "pending" | "in_progress" | "completed" | "cancelled";
  priority: "low" | "medium" | "high" | "urgent";
  pharmacy_id: string;
  assignment_type: "specific_user" | "open_to_all" | "team";
  assigned_to?: string;
  claimed_by?: string;
  claimed_at?: string;
  created_by: string;
  due_date: string;
  completed_at?: string;
  completed_by?: string;
  created_at: string;
  updated_at: string;
  pharmacy_name: string;
  created_by_name: string;
  assigned_to_name?: string;
  claimed_by_name?: string;
  completed_by_name?: string;
  assignment_display?: string;
  urgency_status?: "normal" | "due_soon" | "overdue";
  // Recurring task fields
  is_recurring?: boolean;
  recurring_task_id?: string;
  recurring_task_status?: "active" | "paused" | "completed" | "cancelled";
  recurrence_pattern?: string;
  recurrence_display?: string;
}

interface TaskFilters {
  status: string;
  priority: string;
  type: string;
  assignedTo: string;
  search: string;
}

const Tasks: React.FC = () => {
  const { user, selectedPharmacy } = useAuth();
  const [tasks, setTasks] = useState<TaskWithUser[]>([]);
  const [recurringTasks, setRecurringTasks] = useState<RecurringTask[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskWithUser | null>(null);
  const [_editingTask, _setEditingTask] = useState<TaskWithUser | null>(null);
  const [filters, setFilters] = useState<TaskFilters>({
    status: "all",
    priority: "all",
    type: "all",
    assignedTo: "all",
    search: "",
  });

  // New task form state
  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    type: "other" as
      | "communication"
      | "calibration"
      | "expiry_check"
      | "complaint"
      | "orders"
      | "other",
    priority: "medium" as "low" | "medium" | "high" | "urgent",
    assignment_type: "specific_user" as
      | "specific_user"
      | "open_to_all"
      | "team",
    assigned_to: "",
    due_date: "",
  });

  // New recurring task form state
  const [newRecurringTask, setNewRecurringTask] = useState({
    title: "",
    description: "",
    type: "other" as
      | "communication"
      | "calibration"
      | "expiry_check"
      | "complaint"
      | "orders"
      | "other",
    priority: "medium" as "low" | "medium" | "high" | "urgent",
    assignment_type: "specific_user" as
      | "specific_user"
      | "open_to_all"
      | "team",
    assigned_to: "",
    recurrence_pattern: "monthly" as
      | "daily"
      | "weekly"
      | "monthly"
      | "quarterly"
      | "yearly"
      | "custom",
    recurrence_interval: 1,
    recurrence_days: [] as number[],
    recurrence_day_of_month: 1,
    recurrence_week_of_month: 1,
    recurrence_day_of_week: 1,
    recurrence_month_of_year: [] as number[],
    due_date_offset_days: 0,
    due_time: "09:00",
    start_date: "",
    end_date: "",
  });

  const [statusMenuId, setStatusMenuId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<"kanban" | "list">(() => {
    return (sessionStorage.getItem("tasks_view_mode") as "kanban" | "list") || "kanban";
  });

  // Persist view mode
  useEffect(() => {
    sessionStorage.setItem("tasks_view_mode", viewMode);
  }, [viewMode]);

  // Kanban-specific filters (without status, since columns show that)
  const kanbanFilters: KanbanFilterState = {
    priority: filters.priority,
    type: filters.type,
    assignedTo: filters.assignedTo,
    search: filters.search,
  };

  const handleKanbanFilterChange = (kf: KanbanFilterState) => {
    setFilters((prev) => ({
      ...prev,
      priority: kf.priority,
      type: kf.type,
      assignedTo: kf.assignedTo,
      search: kf.search,
    }));
  };

  // Filtered tasks for kanban (no status filter - all statuses shown in columns)
  const kanbanFilteredTasks = tasks.filter((task) => {
    const matchesPriority =
      filters.priority === "all" || task.priority === filters.priority;
    const matchesType = filters.type === "all" || task.type === filters.type;
    const matchesAssignedTo =
      filters.assignedTo === "all" || task.assigned_to === filters.assignedTo;
    const matchesSearch =
      filters.search === "" ||
      task.title.toLowerCase().includes(filters.search.toLowerCase()) ||
      task.description.toLowerCase().includes(filters.search.toLowerCase());
    return matchesPriority && matchesType && matchesAssignedTo && matchesSearch;
  });

  // Close status dropdown on outside click
  useEffect(() => {
    if (!statusMenuId) return;
    const handleClick = () => setStatusMenuId(null);
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [statusMenuId]);

  const canChangeStatus = (task: TaskWithUser) =>
    user?.role === "admin" ||
    user?.role === "manager" ||
    task.assigned_to === user?.id ||
    task.claimed_by === user?.id;

  // D: Read filter hints set by dashboard stat card clicks
  useEffect(() => {
    const hint = sessionStorage.getItem("task_filter_hint");
    if (hint) {
      try {
        const parsed = JSON.parse(hint);
        setFilters((prev) => ({ ...prev, ...parsed }));
        sessionStorage.removeItem("task_filter_hint");
        return; // skip restoring persisted filters when a hint is present
      } catch {
        // ignore malformed hint
      }
    }
    // Restore persisted filters from previous session
    const saved = sessionStorage.getItem("tasks_filters");
    if (saved) {
      try {
        setFilters(JSON.parse(saved));
      } catch {
        // ignore
      }
    }
  }, []);

  // Persist filters and reset page when filters change
  useEffect(() => {
    sessionStorage.setItem("tasks_filters", JSON.stringify(filters));
    setPage(1);
  }, [filters]);

  useEffect(() => {
    if (selectedPharmacy) {
      fetchTasks();
      fetchRecurringTasks();
      fetchUsers();
    }
  }, [selectedPharmacy]);

  const fetchTasks = async () => {
    if (!selectedPharmacy) {
      return;
    }

    try {
      setLoading(true);
      const { data: tasksData, error } = await supabase
        .from("task_details_enhanced")
        .select("*")
        .eq("pharmacy_id", selectedPharmacy.id)
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }

      // Data already includes user details from the enhanced view
      setTasks(tasksData || []);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.error("Fout bij het laden van taken");
    } finally {
      setLoading(false);
    }
  };

  const fetchRecurringTasks = async () => {
    if (!selectedPharmacy) {
      return;
    }

    try {
      console.log("Fetching recurring tasks for pharmacy:", selectedPharmacy.id);
      
      const { data: recurringTasksData, error } = await supabase
        .from("recurring_task_details")
        .select("*")
        .eq("pharmacy_id", selectedPharmacy.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching recurring tasks:", error);
        throw error;
      }

      console.log("Raw recurring tasks data:", recurringTasksData);
      console.log("Fetched recurring tasks:", recurringTasksData?.length || 0);
      
      // Force clear the state if no data is returned
      if (!recurringTasksData || recurringTasksData.length === 0) {
        console.log("No recurring tasks found, clearing state");
        setRecurringTasks([]);
      } else {
        setRecurringTasks(recurringTasksData);
      }
    } catch (error) {
      console.error("Error fetching recurring tasks:", error);
      toast.error("Fout bij het laden van terugkerende taken");
    }
  };

  const fetchUsers = async () => {
    if (!selectedPharmacy) {
      return;
    }

    try {
      const { data: userPharmacies } = await supabase
        .from(Tables.USER_PHARMACIES)
        .select("user_id")
        .eq("pharmacy_id", selectedPharmacy.id);

      if (userPharmacies) {
        const userIds = userPharmacies.map((up) => up.user_id);
        const { data: usersData } = await supabase
          .from(Tables.USERS)
          .select("*")
          .in("id", userIds);

        setUsers(usersData || []);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const createTask = async () => {
    if (!selectedPharmacy || !user) {
      return;
    }

    try {
      const taskData = {
        title: newTask.title,
        description: newTask.description,
        type: newTask.type,
        priority: newTask.priority,
        assignment_type: newTask.assignment_type,
        assigned_to:
          newTask.assignment_type === "specific_user"
            ? newTask.assigned_to
            : null,
        due_date: newTask.due_date,
        pharmacy_id: selectedPharmacy.id,
        created_by: user.id,
        status: "pending" as const,
      };

      const { error } = await supabase
        .from(Tables.TASKS)
        .insert(taskData)
        .select()
        .single();

      if (error) {
        throw error;
      }

      toast.success("Taak succesvol aangemaakt");
      setShowCreateModal(false);
      setNewTask({
        title: "",
        description: "",
        type: "other",
        priority: "medium",
        assignment_type: "specific_user",
        assigned_to: "",
        due_date: "",
      });
      fetchTasks();
    } catch (error) {
      console.error("Error creating task:", error);
      toast.error("Fout bij het aanmaken van taak");
    }
  };

  const createRecurringTask = async () => {
    if (!selectedPharmacy || !user) {
      return;
    }

    try {
      const recurringTaskData = {
        title: newRecurringTask.title,
        description: newRecurringTask.description,
        type: newRecurringTask.type,
        priority: newRecurringTask.priority,
        pharmacy_id: selectedPharmacy.id,
        recurrence_pattern: newRecurringTask.recurrence_pattern,
        recurrence_interval: newRecurringTask.recurrence_interval,
        recurrence_days: newRecurringTask.recurrence_days.length > 0 ? newRecurringTask.recurrence_days : null,
        recurrence_day_of_month: newRecurringTask.recurrence_day_of_month,
        recurrence_week_of_month: newRecurringTask.recurrence_week_of_month,
        recurrence_day_of_week: newRecurringTask.recurrence_day_of_week,
        recurrence_month_of_year: newRecurringTask.recurrence_month_of_year.length > 0 ? newRecurringTask.recurrence_month_of_year : null,
        assignment_type: newRecurringTask.assignment_type,
        assigned_to: newRecurringTask.assignment_type === "specific_user" ? newRecurringTask.assigned_to : null,
        due_date_offset_days: newRecurringTask.due_date_offset_days,
        due_time: newRecurringTask.due_time,
        start_date: newRecurringTask.start_date,
        end_date: newRecurringTask.end_date || null,
        created_by: user.id,
        status: "active" as const,
      };

      const { error } = await supabase
        .from("recurring_tasks")
        .insert(recurringTaskData)
        .select()
        .single();

      if (error) {
        throw error;
      }

      toast.success("Terugkerende taak succesvol aangemaakt");
      setShowRecurringModal(false);
      setNewRecurringTask({
        title: "",
        description: "",
        type: "other",
        priority: "medium",
        assignment_type: "specific_user",
        assigned_to: "",
        recurrence_pattern: "monthly",
        recurrence_interval: 1,
        recurrence_days: [],
        recurrence_day_of_month: 1,
        recurrence_week_of_month: 1,
        recurrence_day_of_week: 1,
        recurrence_month_of_year: [],
        due_date_offset_days: 0,
        due_time: "09:00",
        start_date: "",
        end_date: "",
      });
      fetchRecurringTasks();
    } catch (error) {
      console.error("Error creating recurring task:", error);
      toast.error("Fout bij het aanmaken van terugkerende taak");
    }
  };

  const toggleRecurringTaskStatus = async (recurringTaskId: string, newStatus: "active" | "paused") => {
    if (!user) {
      return;
    }

    try {
      const { data, error } = await supabase.rpc("toggle_recurring_task_status", {
        p_recurring_task_id: recurringTaskId,
        p_user_id: user.id,
        p_new_status: newStatus,
      });

      if (error) {
        throw error;
      }

      const result = data as {
        success: boolean;
        error?: string;
        message?: string;
      };

      if (result.success) {
        toast.success(result.message || "Status bijgewerkt");
        fetchRecurringTasks();
      } else {
        toast.error(result.error || "Fout bij het bijwerken van status");
      }
    } catch (error) {
      console.error("Error toggling recurring task status:", error);
      toast.error("Fout bij het bijwerken van status");
    }
  };

  const generateRecurringTasks = async () => {
    try {
      const { data, error } = await supabase.rpc("generate_recurring_tasks");

      if (error) {
        throw error;
      }

      const tasksGenerated = data as number;
      toast.success(`${tasksGenerated} nieuwe taken gegenereerd`);
      fetchTasks();
      fetchRecurringTasks();
    } catch (error) {
      console.error("Error generating recurring tasks:", error);
      toast.error("Fout bij het genereren van terugkerende taken");
    }
  };

  const deleteRecurringTask = async (recurringTaskId: string) => {
    if (!user) {
      return;
    }

    if (!confirm("Weet je zeker dat je deze terugkerende taak wilt verwijderen? Dit zal ook alle gegenereerde taken verwijderen.")) {
      return;
    }

    console.log("Attempting to delete recurring task:", recurringTaskId);
    console.log("User ID:", user.id);
    console.log("User role:", user.role);

    try {
      const { data, error } = await supabase.rpc("delete_recurring_task", {
        p_recurring_task_id: recurringTaskId,
        p_user_id: user.id,
      });

      console.log("Delete function response:", { data, error });

      if (error) {
        console.error("Database error:", error);
        throw error;
      }

      const result = data as {
        success: boolean;
        error?: string;
        message?: string;
      };

      console.log("Delete result:", result);

      if (result.success) {
        toast.success(result.message || "Terugkerende taak verwijderd");
        // Immediately clear the state and refresh
        console.log("Clearing state and refreshing data after deletion...");
        setRecurringTasks([]); // Clear the state first
        await fetchRecurringTasks();
        await fetchTasks();
        
        // Also try a second refresh after a short delay
        setTimeout(async () => {
          console.log("Second refresh after deletion...");
          await fetchRecurringTasks();
          await fetchTasks();
        }, 1000);
      } else {
        console.error("Delete failed:", result.error);
        toast.error(result.error || "Fout bij het verwijderen van terugkerende taak");
      }
    } catch (error) {
      console.error("Error deleting recurring task:", error);
      toast.error("Fout bij het verwijderen van terugkerende taak");
    }
  };

  // Compute the next N occurrence dates for a recurring task
  const computeNextOccurrences = (task: RecurringTask, count: number): Date[] => {
    if (!task.next_generation_date) return [];
    const occurrences: Date[] = [];
    let current = parseISO(task.next_generation_date);
    for (let i = 0; i < count; i++) {
      occurrences.push(current);
      switch (task.recurrence_pattern) {
        case "daily":     current = addDays(current, task.recurrence_interval); break;
        case "weekly":    current = addWeeks(current, task.recurrence_interval); break;
        case "monthly":   current = addMonths(current, task.recurrence_interval); break;
        case "quarterly": current = addMonths(current, task.recurrence_interval * 3); break;
        case "yearly":    current = addYears(current, task.recurrence_interval); break;
        default: return occurrences;
      }
    }
    return occurrences;
  };

  // Skip the upcoming occurrence and advance to the one after
  const skipOccurrence = async (recurringTaskId: string) => {
    const task = recurringTasks.find((t) => t.id === recurringTaskId);
    if (!task || !task.next_generation_date) return;

    let skipTo: Date;
    const current = parseISO(task.next_generation_date);
    switch (task.recurrence_pattern) {
      case "daily":     skipTo = addDays(current, task.recurrence_interval); break;
      case "weekly":    skipTo = addWeeks(current, task.recurrence_interval); break;
      case "monthly":   skipTo = addMonths(current, task.recurrence_interval); break;
      case "quarterly": skipTo = addMonths(current, task.recurrence_interval * 3); break;
      case "yearly":    skipTo = addYears(current, task.recurrence_interval); break;
      default: return;
    }

    const { error } = await supabase
      .from("recurring_tasks")
      .update({ next_generation_date: format(skipTo, "yyyy-MM-dd") })
      .eq("id", recurringTaskId);

    if (error) {
      toast.error("Kon herhaling niet overslaan");
      return;
    }
    toast.success("Eenmalige herhaling overgeslagen");
    fetchRecurringTasks();
  };

  const updateTaskStatus = async (
    taskId: string,
    status: "pending" | "in_progress" | "completed" | "cancelled",
  ) => {
    try {
      const updateData: {
        status: "pending" | "in_progress" | "completed" | "cancelled";
        completed_at?: string;
      } = {
        status,
      };
      if (status === "completed") {
        updateData.completed_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from(Tables.TASKS)
        .update(updateData)
        .eq("id", taskId);

      if (error) {
        throw error;
      }

      toast.success("Taak status bijgewerkt");

      if (status === "completed" || status === "cancelled") {
        const task = tasks.find((t) => t.id === taskId);
        if (task && user && selectedPharmacy) {
          await logAuditEvent({
            pharmacy_id: selectedPharmacy.id,
            user_id: user.id,
            user_name: user.full_name,
            action: status,
            entity_type: "task",
            entity_id: taskId,
            entity_label: task.title,
          });
        }
      }

      fetchTasks();
    } catch (error) {
      console.error("Error updating task status:", error);
      toast.error("Fout bij het bijwerken van taak status");
    }
  };

  const claimTask = async (taskId: string) => {
    if (!user) {
      return;
    }

    try {
      const { data, error } = await supabase.rpc("claim_open_task", {
        p_task_id: taskId,
        p_user_id: user.id,
      });

      if (error) {
        console.error("Error claiming task:", error);
        toast.error("Failed to claim task");
        return;
      }

      const result = data as {
        success: boolean;
        error?: string;
        message?: string;
      };

      if (result.success) {
        toast.success("Task claimed successfully");
        fetchTasks();
      } else {
        toast.error(result.error || "Failed to claim task");
      }
    } catch (error) {
      console.error("Error claiming task:", error);
      toast.error("Failed to claim task");
    }
  };

  const unclaimTask = async (taskId: string) => {
    if (!user) {
      return;
    }

    try {
      const { data, error } = await supabase.rpc("unclaim_task", {
        p_task_id: taskId,
        p_user_id: user.id,
      });

      if (error) {
        console.error("Error unclaiming task:", error);
        toast.error("Failed to unclaim task");
        return;
      }

      const result = data as {
        success: boolean;
        error?: string;
        message?: string;
      };

      if (result.success) {
        toast.success("Task unclaimed successfully");
        fetchTasks();
      } else {
        toast.error(result.error || "Failed to unclaim task");
      }
    } catch (error) {
      console.error("Error unclaiming task:", error);
      toast.error("Failed to unclaim task");
    }
  };

  const completeTask = async (taskId: string) => {
    if (!user) {
      return;
    }

    try {
      const { data, error } = await supabase.rpc("complete_task", {
        p_task_id: taskId,
        p_user_id: user.id,
      });

      if (error) {
        console.error("Error completing task:", error);
        toast.error("Failed to complete task");
        return;
      }

      const result = data as {
        success: boolean;
        error?: string;
        message?: string;
      };

      if (result.success) {
        toast.success("Task completed successfully");
        const task = tasks.find((t) => t.id === taskId);
        if (task && selectedPharmacy) {
          await logAuditEvent({
            pharmacy_id: selectedPharmacy.id,
            user_id: user.id,
            user_name: user.full_name,
            action: "completed",
            entity_type: "task",
            entity_id: taskId,
            entity_label: task.title,
          });
        }
        fetchTasks();
      } else {
        toast.error(result.error || "Failed to complete task");
      }
    } catch (error) {
      console.error("Error completing task:", error);
      toast.error("Failed to complete task");
    }
  };

  const deleteTask = async (taskId: string) => {
    if (!confirm("Weet je zeker dat je deze taak wilt verwijderen?")) {
      return;
    }

    try {
      const { error, count } = await supabase
        .from(Tables.TASKS)
        .delete({ count: "exact" })
        .eq("id", taskId);

      if (error) {
        throw error;
      }

      if (count === 0) {
        toast.error("Taak kon niet worden verwijderd. Controleer je rechten.");
        return;
      }

      // Optimistically remove from local state immediately
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      toast.success("Taak verwijderd");
      fetchTasks();
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error("Fout bij het verwijderen van taak");
    }
  };

  const handleExportCsv = () => {
    const headers = [
      "Titel",
      "Type",
      "Status",
      "Prioriteit",
      "Toegewezen aan",
      "Vervaldatum",
      "Aangemaakt op",
      "Omschrijving",
    ];
    const rows = filteredTasks.map((t) => [
      t.title,
      t.type,
      t.status,
      t.priority,
      t.assigned_to_name || t.claimed_by_name || "",
      t.due_date ? format(parseISO(t.due_date), "dd-MM-yyyy") : "",
      format(parseISO(t.created_at), "dd-MM-yyyy"),
      t.description,
    ]);
    exportToCsv(
      `taken_${format(new Date(), "yyyy-MM-dd")}.csv`,
      headers,
      rows,
    );
    toast.success("CSV geëxporteerd");
  };

  const filteredTasks = tasks.filter((task) => {
    let matchesStatus: boolean;
    if (filters.status === "all") {
      matchesStatus = true;
    } else if (filters.status === "overdue") {
      matchesStatus =
        (task.status === "pending" || task.status === "in_progress") &&
        !!task.due_date &&
        new Date(task.due_date) < new Date();
    } else {
      matchesStatus = task.status === filters.status;
    }
    const matchesPriority =
      filters.priority === "all" || task.priority === filters.priority;
    const matchesType = filters.type === "all" || task.type === filters.type;
    const matchesAssignedTo =
      filters.assignedTo === "all" || task.assigned_to === filters.assignedTo;
    const matchesSearch =
      filters.search === "" ||
      task.title.toLowerCase().includes(filters.search.toLowerCase()) ||
      task.description.toLowerCase().includes(filters.search.toLowerCase());

    return (
      matchesStatus &&
      matchesPriority &&
      matchesType &&
      matchesAssignedTo &&
      matchesSearch
    );
  });

  const getTaskTypeIcon = (type: string) => {
    switch (type) {
      case "communication":
        return ChatBubbleLeftRightIcon;
      case "calibration":
        return ScaleIcon;
      case "expiry_check":
        return CalendarDaysIcon;
      case "complaint":
        return ExclamationTriangleIcon;
      case "orders":
        return ClipboardDocumentListIcon;
      default:
        return ClockIcon;
    }
  };

  const getTaskTypeLabel = (type: string) => {
    switch (type) {
      case "communication":
        return "Communicatie";
      case "calibration":
        return "Controle balansen";
      case "expiry_check":
        return "vervaldata";
      case "complaint":
        return "Klacht";
      case "orders":
        return "Bestellingen";
      default:
        return "Overige";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "text-red-700 bg-red-50 border-red-200";
      case "high":
        return "text-orange-700 bg-orange-50 border-orange-200";
      case "medium":
        return "text-yellow-700 bg-yellow-50 border-yellow-200";
      case "low":
        return "text-green-700 bg-green-50 border-green-200";
      default:
        return "text-gray-700 bg-gray-50 border-gray-200";
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "Urgent";
      case "high":
        return "Hoog";
      case "medium":
        return "Gemiddeld";
      case "low":
        return "Laag";
      default:
        return "Onbekend";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "text-green-700 bg-green-50 border-green-200";
      case "in_progress":
        return "text-primary-700 bg-primary-50 border-primary-200";
      case "pending":
        return "text-yellow-700 bg-yellow-50 border-yellow-200";
      case "cancelled":
        return "text-gray-700 bg-gray-50 border-gray-200";
      default:
        return "text-gray-700 bg-gray-50 border-gray-200";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "completed":
        return "Voltooid";
      case "in_progress":
        return "Bezig";
      case "pending":
        return "Openstaand";
      case "cancelled":
        return "Geannuleerd";
      default:
        return "Onbekend";
    }
  };

  const formatDueDate = (dateString: string) => {
    const date = parseISO(dateString);
    if (isToday(date)) {
      return "Vandaag";
    }
    if (isTomorrow(date)) {
      return "Morgen";
    }
    if (isPast(date)) {
      return "Verlopen";
    }
    return format(date, "d MMM yyyy", { locale: nl });
  };

  const getDueDateColor = (dateString: string) => {
    const date = parseISO(dateString);
    if (isPast(date)) {
      return "text-red-600";
    }
    if (isToday(date)) {
      return "text-orange-600";
    }
    if (isTomorrow(date)) {
      return "text-yellow-600";
    }
    return "text-gray-600";
  };

  if (loading) {
    return (
      <div className="p-6 sm:p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Taken</h1>
          <p className="mt-2 text-sm text-gray-700">
            Beheer en volg alle QA taken voor {selectedPharmacy?.name}
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          {/* View toggle */}
          <div className="inline-flex rounded-md shadow-sm">
            <button
              onClick={() => setViewMode("kanban")}
              className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-l-md border ${
                viewMode === "kanban"
                  ? "bg-primary-50 text-primary-700 border-primary-300 z-10"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
              title="Kanban weergave"
            >
              <ViewColumnsIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-r-md border -ml-px ${
                viewMode === "list"
                  ? "bg-primary-50 text-primary-700 border-primary-300 z-10"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
              title="Lijst weergave"
            >
              <ListBulletIcon className="h-4 w-4" />
            </button>
          </div>
          <button
            onClick={handleExportCsv}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            title="Exporteer gefilterde taken naar CSV"
          >
            <ArrowDownTrayIcon className="-ml-1 mr-2 h-4 w-4" />
            CSV
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Nieuwe Taak
          </button>
          <button
            onClick={() => setShowRecurringModal(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <ArrowPathIcon className="-ml-1 mr-2 h-5 w-5" />
            Terugkerende Taak
          </button>
          <button
            onClick={generateRecurringTasks}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <ClockIcon className="-ml-1 mr-2 h-5 w-5" />
            Genereer Taken
          </button>
        </div>
      </div>

      {/* Kanban View */}
      {viewMode === "kanban" && (
        <>
          <KanbanFilters
            filters={kanbanFilters}
            onFilterChange={handleKanbanFilterChange}
            users={users}
          />
          <KanbanBoard
            tasks={kanbanFilteredTasks}
            currentUserId={user?.id}
            canChangeStatus={canChangeStatus}
            onUpdateTaskStatus={updateTaskStatus}
            onCompleteTask={completeTask}
            onClaimTask={claimTask}
            onUnclaimTask={unclaimTask}
            onClickCard={(task) => {
              setSelectedTask(task);
              setShowTaskModal(true);
            }}
          />
        </>
      )}

      {/* List View - Filters */}
      {viewMode === "list" && (
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Zoeken..."
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
                className="pl-10 block w-full border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value })
              }
              className="block w-full border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="all">Alle statussen</option>
              <option value="overdue">Verlopen</option>
              <option value="pending">Openstaand</option>
              <option value="in_progress">Bezig</option>
              <option value="completed">Voltooid</option>
              <option value="cancelled">Geannuleerd</option>
            </select>
          </div>

          {/* Priority Filter */}
          <div>
            <select
              value={filters.priority}
              onChange={(e) =>
                setFilters({ ...filters, priority: e.target.value })
              }
              className="block w-full border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="all">Alle prioriteiten</option>
              <option value="urgent">Urgent</option>
              <option value="high">Hoog</option>
              <option value="medium">Gemiddeld</option>
              <option value="low">Laag</option>
            </select>
          </div>

          {/* Type Filter */}
          <div>
            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              className="block w-full border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="all">Alle types</option>
              <option value="communication">Communicatie</option>
              <option value="calibration">Kalibratie</option>
              <option value="expiry_check">Houdbaarheidcontrole</option>
              <option value="complaint">Klacht</option>
              <option value="orders">Bestellingen</option>
              <option value="other">Overig</option>
            </select>
          </div>

          {/* Assigned To Filter */}
          <div>
            <select
              value={filters.assignedTo}
              onChange={(e) =>
                setFilters({ ...filters, assignedTo: e.target.value })
              }
              className="block w-full border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="all">Alle medewerkers</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.full_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      )}

      {/* List View - Task List */}
      {viewMode === "list" && (
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        {filteredTasks.length === 0 ? (
          <div className="text-center py-12">
            <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              Geen taken gevonden
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {filters.search ||
              filters.status !== "all" ||
              filters.priority !== "all" ||
              filters.type !== "all" ||
              filters.assignedTo !== "all"
                ? "Pas je filters aan om meer taken te zien."
                : "Begin met het aanmaken van je eerste taak."}
            </p>
            {!(
              filters.search ||
              filters.status !== "all" ||
              filters.priority !== "all" ||
              filters.type !== "all" ||
              filters.assignedTo !== "all"
            ) && (
              <div className="mt-6">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                  Nieuwe Taak
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredTasks.slice((page - 1) * TASKS_PAGE_SIZE, page * TASKS_PAGE_SIZE).map((task) => {
              const TaskIcon = getTaskTypeIcon(task.type);
              return (
                <div
                  key={task.id}
                  className="p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className="flex-shrink-0">
                        <div className="p-2 bg-gray-100 rounded-lg">
                          <TaskIcon className="h-5 w-5 text-gray-600" />
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="text-lg font-medium text-gray-900 truncate">
                            {task.title}
                          </h3>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(task.priority)}`}
                          >
                            {getPriorityLabel(task.priority)}
                          </span>
                          {canChangeStatus(task) ? (
                            <div
                              className="relative"
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setStatusMenuId(
                                    statusMenuId === task.id ? null : task.id,
                                  );
                                }}
                                className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border hover:opacity-80 transition-opacity ${getStatusColor(task.status)}`}
                              >
                                {getStatusLabel(task.status)}
                                <ChevronDownIcon className="h-3 w-3" />
                              </button>
                              {statusMenuId === task.id && (
                                <div className="absolute left-0 top-full mt-1 z-20 w-36 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 py-1">
                                  {(
                                    [
                                      "pending",
                                      "in_progress",
                                      "completed",
                                      "cancelled",
                                    ] as const
                                  )
                                    .filter((s) => s !== task.status)
                                    .map((s) => (
                                      <button
                                        key={s}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          updateTaskStatus(task.id, s);
                                          setStatusMenuId(null);
                                        }}
                                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center"
                                      >
                                        <span
                                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(s)}`}
                                        >
                                          {getStatusLabel(s)}
                                        </span>
                                      </button>
                                    ))}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(task.status)}`}
                            >
                              {getStatusLabel(task.status)}
                            </span>
                          )}
                        </div>

                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                          {task.description}
                        </p>

                        <div className="flex items-center space-x-6 text-sm text-gray-500">
                          <div className="flex items-center">
                            <span className="font-medium">Type:</span>
                            <span className="ml-1">
                              {getTaskTypeLabel(task.type)}
                            </span>
                          </div>

                          <div className="flex items-center">
                            <UserIcon className="h-4 w-4 mr-1" />
                            <span>
                              {task.assignment_type === "open_to_all" &&
                                !task.claimed_by && (
                                  <span className="text-green-600 font-medium">
                                    Open voor iedereen
                                  </span>
                                )}
                              {task.assignment_type === "open_to_all" &&
                                task.claimed_by && (
                                  <span className="text-blue-600">
                                    Opgepakt door{" "}
                                    {task.claimed_by_name || "Onbekend"}
                                  </span>
                                )}
                              {task.assignment_type === "specific_user" &&
                                task.assigned_to_name && (
                                  <span>
                                    Toegewezen aan {task.assigned_to_name}
                                  </span>
                                )}
                              {task.assignment_type === "team" && (
                                <span className="text-purple-600">
                                  Team taak
                                </span>
                              )}
                            </span>
                          </div>

                          <div className="flex items-center">
                            <CalendarDaysIcon className="h-4 w-4 mr-1" />
                            <span className={getDueDateColor(task.due_date)}>
                              {formatDueDate(task.due_date)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      {/* Open-to-all: show claim button if unclaimed */}
                      {task.assignment_type === "open_to_all" &&
                        task.status === "pending" &&
                        !task.claimed_by && (
                          <button
                            onClick={() => claimTask(task.id)}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                          >
                            Pak aan
                          </button>
                        )}
                      {/* Open-to-all: show release button for claimer */}
                      {task.assignment_type === "open_to_all" &&
                        task.claimed_by === user?.id && (
                          <button
                            onClick={() => unclaimTask(task.id)}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            Laat los
                          </button>
                        )}

                      {/* Detail view */}
                      <button
                        onClick={() => {
                          setSelectedTask(task);
                          setShowTaskModal(true);
                        }}
                        className="p-2 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 rounded-md"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <Pagination
          page={page}
          pageSize={TASKS_PAGE_SIZE}
          total={filteredTasks.length}
          onPageChange={setPage}
        />
      </div>
      )}

      {/* Recurring Tasks Section */}
      {recurringTasks.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Terugkerende Taken</h2>
            <span className="text-sm text-gray-500">{recurringTasks.length} actief</span>
          </div>
          <div className="bg-white shadow-sm rounded-lg border border-gray-200">
            <div className="divide-y divide-gray-200">
              {recurringTasks.map((recurringTask) => {
                const TaskIcon = getTaskTypeIcon(recurringTask.type);
                const isAdmin = user?.role === "admin" || user?.role === "manager";
                const nextOccurrences = computeNextOccurrences(recurringTask, 3);
                const daysUntilNext = recurringTask.next_generation_date
                  ? differenceInDays(parseISO(recurringTask.next_generation_date), new Date())
                  : null;
                const isDueSoon = daysUntilNext !== null && daysUntilNext >= 0 && daysUntilNext <= 7;
                return (
                  <div
                    key={recurringTask.id}
                    className="p-6 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        <div className="flex-shrink-0">
                          <div className="p-2 bg-gray-100 rounded-lg">
                            <TaskIcon className="h-5 w-5 text-gray-600" />
                          </div>
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="text-lg font-medium text-gray-900 truncate">
                              {recurringTask.title}
                            </h3>
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(recurringTask.priority)}`}
                            >
                              {getPriorityLabel(recurringTask.priority)}
                            </span>
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                                recurringTask.status === "active"
                                  ? "text-green-700 bg-green-50 border-green-200"
                                  : "text-yellow-700 bg-yellow-50 border-yellow-200"
                              }`}
                            >
                              {recurringTask.status === "active" ? "Actief" : "Gepauzeerd"}
                            </span>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border text-blue-700 bg-blue-50 border-blue-200">
                              <ArrowPathIcon className="h-3 w-3 mr-1" />
                              Terugkerend
                            </span>
                            {isDueSoon && recurringTask.status === "active" && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border text-amber-700 bg-amber-50 border-amber-200">
                                <ClockIcon className="h-3 w-3 mr-1" />
                                Binnenkort
                              </span>
                            )}
                          </div>

                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                            {recurringTask.description}
                          </p>

                          <div className="flex items-center space-x-6 text-sm text-gray-500">
                            <div className="flex items-center">
                              <span className="font-medium">Type:</span>
                              <span className="ml-1">
                                {getTaskTypeLabel(recurringTask.type)}
                              </span>
                            </div>

                            <div className="flex items-center">
                              <ArrowPathIcon className="h-4 w-4 mr-1" />
                              <span>
                                {recurringTask.recurrence_pattern === "daily" && `Dagelijks (elke ${recurringTask.recurrence_interval} dag${recurringTask.recurrence_interval > 1 ? "en" : ""})`}
                                {recurringTask.recurrence_pattern === "weekly" && `Wekelijks (elke ${recurringTask.recurrence_interval} week${recurringTask.recurrence_interval > 1 ? "en" : ""})`}
                                {recurringTask.recurrence_pattern === "monthly" && `Maandelijks (elke ${recurringTask.recurrence_interval} maand${recurringTask.recurrence_interval > 1 ? "en" : ""})`}
                                {recurringTask.recurrence_pattern === "quarterly" && `Kwartaal (elke ${recurringTask.recurrence_interval} kwartaal${recurringTask.recurrence_interval > 1 ? "en" : ""})`}
                                {recurringTask.recurrence_pattern === "yearly" && `Jaarlijks (elke ${recurringTask.recurrence_interval} jaar${recurringTask.recurrence_interval > 1 ? "en" : ""})`}
                              </span>
                            </div>

                            <div className="flex items-center">
                              <UserIcon className="h-4 w-4 mr-1" />
                              <span>
                                {recurringTask.assignment_type === "specific_user" && recurringTask.assigned_to && (
                                  <span>Toegewezen aan {users.find(u => u.id === recurringTask.assigned_to)?.full_name || "Onbekend"}</span>
                                )}
                                {recurringTask.assignment_type === "open_to_all" && (
                                  <span className="text-green-600">Open voor iedereen</span>
                                )}
                                {recurringTask.assignment_type === "team" && (
                                  <span className="text-purple-600">Team taak</span>
                                )}
                              </span>
                            </div>

                            <div className="flex items-center">
                              <CalendarDaysIcon className="h-4 w-4 mr-1" />
                              <span>
                                {recurringTask.total_generated_count} gegenereerd
                              </span>
                            </div>
                          </div>

                          {nextOccurrences.length > 0 && (
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-xs text-gray-400">Volgende:</span>
                              {nextOccurrences.map((date, i) => (
                                <span
                                  key={i}
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                    i === 0 && isDueSoon
                                      ? "bg-amber-100 text-amber-700"
                                      : "bg-gray-100 text-gray-600"
                                  }`}
                                >
                                  {format(date, "d MMM", { locale: nl })}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        {isAdmin && recurringTask.status === "active" && recurringTask.next_generation_date && (
                          <button
                            onClick={() => skipOccurrence(recurringTask.id)}
                            title="Sla de volgende herhaling over"
                            className="inline-flex items-center px-3 py-2 border border-amber-300 text-sm leading-4 font-medium rounded-md text-amber-700 bg-white hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                          >
                            <ForwardIcon className="h-4 w-4 mr-1" />
                            Overslaan
                          </button>
                        )}
                        {recurringTask.status === "active" ? (
                          <button
                            onClick={() => toggleRecurringTaskStatus(recurringTask.id, "paused")}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            <PauseIcon className="h-4 w-4 mr-1" />
                            Pauzeren
                          </button>
                        ) : (
                          <button
                            onClick={() => toggleRecurringTaskStatus(recurringTask.id, "active")}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                          >
                            <PlayIcon className="h-4 w-4 mr-1" />
                            Hervatten
                          </button>
                        )}
                        
                        {isAdmin && (
                          <button
                            onClick={() => deleteRecurringTask(recurringTask.id)}
                            className="inline-flex items-center px-3 py-2 border border-red-300 text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            <XMarkIcon className="h-4 w-4 mr-1" />
                            Verwijder
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Create Task Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Nieuwe Taak Aanmaken
                </h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Sluiten</span>✕
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Titel
                  </label>
                  <input
                    type="text"
                    value={newTask.title}
                    onChange={(e) =>
                      setNewTask({ ...newTask, title: e.target.value })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Titel van de taak"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Beschrijving
                  </label>
                  <textarea
                    rows={3}
                    value={newTask.description}
                    onChange={(e) =>
                      setNewTask({ ...newTask, description: e.target.value })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Gedetailleerde beschrijving van de taak"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Type
                    </label>
                    <select
                      value={newTask.type}
                      onChange={(e) =>
                        setNewTask({
                          ...newTask,
                          type: e.target.value as
                            | "communication"
                            | "calibration"
                            | "expiry_check"
                            | "complaint"
                            | "orders"
                            | "other",
                        })
                      }
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    >
                      <option value="communication">Communicatie</option>
                      <option value="calibration">Kalibratie</option>
                      <option value="expiry_check">Houdbaarheidcontrole</option>
                      <option value="complaint">Klacht</option>
                      <option value="orders">Bestellingen</option>
                      <option value="other">Overig</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Prioriteit
                    </label>
                    <select
                      value={newTask.priority}
                      onChange={(e) =>
                        setNewTask({
                          ...newTask,
                          priority: e.target.value as
                            | "low"
                            | "medium"
                            | "high"
                            | "urgent",
                        })
                      }
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    >
                      <option value="low">Laag</option>
                      <option value="medium">Gemiddeld</option>
                      <option value="high">Hoog</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Toewijzing Type
                  </label>
                  <select
                    value={newTask.assignment_type}
                    onChange={(e) =>
                      setNewTask({
                        ...newTask,
                        assignment_type: e.target.value as
                          | "specific_user"
                          | "open_to_all"
                          | "team",
                        assigned_to:
                          e.target.value === "specific_user"
                            ? newTask.assigned_to
                            : "",
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  >
                    <option value="specific_user">Specifieke Medewerker</option>
                    <option value="open_to_all">
                      Open voor Iedereen (Wie het Eerst Doet)
                    </option>
                    <option value="team">Team Taak</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {newTask.assignment_type === "open_to_all" &&
                      "Elke medewerker kan deze taak oppakken"}
                    {newTask.assignment_type === "specific_user" &&
                      "Taak wordt toegewezen aan een specifieke persoon"}
                    {newTask.assignment_type === "team" &&
                      "Taak is voor het hele team"}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {newTask.assignment_type === "specific_user" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Toegewezen aan
                      </label>
                      <select
                        value={newTask.assigned_to}
                        onChange={(e) =>
                          setNewTask({
                            ...newTask,
                            assigned_to: e.target.value,
                          })
                        }
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      >
                        <option value="">Selecteer medewerker</option>
                        {users.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.full_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Vervaldatum
                    </label>
                    <input
                      type="date"
                      value={newTask.due_date}
                      onChange={(e) =>
                        setNewTask({ ...newTask, due_date: e.target.value })
                      }
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Annuleren
                </button>
                <button
                  onClick={createTask}
                  disabled={
                    !newTask.title ||
                    !newTask.description ||
                    !newTask.due_date ||
                    (newTask.assignment_type === "specific_user" &&
                      !newTask.assigned_to)
                  }
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Taak Aanmaken
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Recurring Task Modal */}
      {showRecurringModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Nieuwe Terugkerende Taak Aanmaken
                </h3>
                <button
                  onClick={() => setShowRecurringModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Sluiten</span>✕
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Titel
                  </label>
                  <input
                    type="text"
                    value={newRecurringTask.title}
                    onChange={(e) =>
                      setNewRecurringTask({ ...newRecurringTask, title: e.target.value })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Titel van de terugkerende taak"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Beschrijving
                  </label>
                  <textarea
                    rows={3}
                    value={newRecurringTask.description}
                    onChange={(e) =>
                      setNewRecurringTask({ ...newRecurringTask, description: e.target.value })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Gedetailleerde beschrijving van de terugkerende taak"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Type
                    </label>
                    <select
                      value={newRecurringTask.type}
                      onChange={(e) =>
                        setNewRecurringTask({
                          ...newRecurringTask,
                          type: e.target.value as
                            | "communication"
                            | "calibration"
                            | "expiry_check"
                            | "complaint"
                            | "orders"
                            | "other",
                        })
                      }
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    >
                      <option value="communication">Communicatie</option>
                      <option value="calibration">Kalibratie</option>
                      <option value="expiry_check">Houdbaarheidcontrole</option>
                      <option value="complaint">Klacht</option>
                      <option value="orders">Bestellingen</option>
                      <option value="other">Overig</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Prioriteit
                    </label>
                    <select
                      value={newRecurringTask.priority}
                      onChange={(e) =>
                        setNewRecurringTask({
                          ...newRecurringTask,
                          priority: e.target.value as
                            | "low"
                            | "medium"
                            | "high"
                            | "urgent",
                        })
                      }
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    >
                      <option value="low">Laag</option>
                      <option value="medium">Gemiddeld</option>
                      <option value="high">Hoog</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Herhaling Patroon
                    </label>
                    <select
                      value={newRecurringTask.recurrence_pattern}
                      onChange={(e) =>
                        setNewRecurringTask({
                          ...newRecurringTask,
                          recurrence_pattern: e.target.value as
                            | "daily"
                            | "weekly"
                            | "monthly"
                            | "quarterly"
                            | "yearly"
                            | "custom",
                        })
                      }
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    >
                      <option value="daily">Dagelijks</option>
                      <option value="weekly">Wekelijks</option>
                      <option value="monthly">Maandelijks</option>
                      <option value="quarterly">Kwartaal</option>
                      <option value="yearly">Jaarlijks</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Interval
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={newRecurringTask.recurrence_interval}
                      onChange={(e) =>
                        setNewRecurringTask({
                          ...newRecurringTask,
                          recurrence_interval: parseInt(e.target.value) || 1,
                        })
                      }
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                </div>

                {newRecurringTask.recurrence_pattern === "monthly" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Dag van de maand
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={newRecurringTask.recurrence_day_of_month}
                      onChange={(e) =>
                        setNewRecurringTask({
                          ...newRecurringTask,
                          recurrence_day_of_month: parseInt(e.target.value) || 1,
                        })
                      }
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Startdatum
                    </label>
                    <input
                      type="date"
                      value={newRecurringTask.start_date}
                      onChange={(e) =>
                        setNewRecurringTask({ ...newRecurringTask, start_date: e.target.value })
                      }
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Einddatum (optioneel)
                    </label>
                    <input
                      type="date"
                      value={newRecurringTask.end_date}
                      onChange={(e) =>
                        setNewRecurringTask({ ...newRecurringTask, end_date: e.target.value })
                      }
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Vervaldatum Offset (dagen)
                    </label>
                    <input
                      type="number"
                      value={newRecurringTask.due_date_offset_days}
                      onChange={(e) =>
                        setNewRecurringTask({
                          ...newRecurringTask,
                          due_date_offset_days: parseInt(e.target.value) || 0,
                        })
                      }
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="0 = op de herhalingsdatum, -2 = 2 dagen ervoor"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Tijd
                    </label>
                    <input
                      type="time"
                      value={newRecurringTask.due_time}
                      onChange={(e) =>
                        setNewRecurringTask({ ...newRecurringTask, due_time: e.target.value })
                      }
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Toewijzing Type
                  </label>
                  <select
                    value={newRecurringTask.assignment_type}
                    onChange={(e) =>
                      setNewRecurringTask({
                        ...newRecurringTask,
                        assignment_type: e.target.value as
                          | "specific_user"
                          | "open_to_all"
                          | "team",
                        assigned_to:
                          e.target.value === "specific_user"
                            ? newRecurringTask.assigned_to
                            : "",
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  >
                    <option value="specific_user">Specifieke Medewerker</option>
                    <option value="open_to_all">
                      Open voor Iedereen (Wie het Eerst Doet)
                    </option>
                    <option value="team">Team Taak</option>
                  </select>
                </div>

                {newRecurringTask.assignment_type === "specific_user" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Toegewezen aan
                    </label>
                    <select
                      value={newRecurringTask.assigned_to}
                      onChange={(e) =>
                        setNewRecurringTask({
                          ...newRecurringTask,
                          assigned_to: e.target.value,
                        })
                      }
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    >
                      <option value="">Selecteer medewerker</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.full_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => setShowRecurringModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Annuleren
                </button>
                <button
                  onClick={createRecurringTask}
                  disabled={
                    !newRecurringTask.title ||
                    !newRecurringTask.description ||
                    !newRecurringTask.start_date ||
                    (newRecurringTask.assignment_type === "specific_user" &&
                      !newRecurringTask.assigned_to)
                  }
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Terugkerende Taak Aanmaken
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Task Detail Modal */}
      {showTaskModal && selectedTask && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    {React.createElement(getTaskTypeIcon(selectedTask.type), {
                      className: "h-6 w-6 text-gray-600",
                    })}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      {selectedTask.title}
                    </h3>
                    <div className="flex items-center space-x-2 mt-1">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(selectedTask.priority)}`}
                      >
                        {getPriorityLabel(selectedTask.priority)}
                      </span>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(selectedTask.status)}`}
                      >
                        {getStatusLabel(selectedTask.status)}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowTaskModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Sluiten</span>✕
                </button>
              </div>

              <div className="space-y-6">
                {/* Task Details */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Beschrijving
                  </h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {selectedTask.description}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">
                      Details
                    </h4>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Type:</dt>
                        <dd className="text-gray-900">
                          {getTaskTypeLabel(selectedTask.type)}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Vervaldatum:</dt>
                        <dd
                          className={`font-medium ${getDueDateColor(selectedTask.due_date)}`}
                        >
                          {formatDueDate(selectedTask.due_date)}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Aangemaakt:</dt>
                        <dd className="text-gray-900">
                          {format(
                            parseISO(selectedTask.created_at),
                            "d MMM yyyy HH:mm",
                            { locale: nl },
                          )}
                        </dd>
                      </div>
                      {selectedTask.completed_at && (
                        <div className="flex justify-between">
                          <dt className="text-gray-500">Voltooid:</dt>
                          <dd className="text-gray-900">
                            {format(
                              parseISO(selectedTask.completed_at),
                              "d MMM yyyy HH:mm",
                              { locale: nl },
                            )}
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">
                      Personen
                    </h4>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Toegewezen aan:</dt>
                        <dd className="text-gray-900">
                          {selectedTask.assigned_to_name || "Niet toegewezen"}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Aangemaakt door:</dt>
                        <dd className="text-gray-900">
                          {selectedTask.created_by_name || "Onbekend"}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>

                {/* Status Update Actions */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Acties
                  </h4>
                  <div className="flex space-x-3">
                    {selectedTask.assignment_type === "open_to_all" &&
                      selectedTask.status === "pending" &&
                      !selectedTask.claimed_by && (
                        <button
                          onClick={() => {
                            claimTask(selectedTask.id);
                            setShowTaskModal(false);
                          }}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          <ClockIcon className="h-4 w-4 mr-1" />
                          Pak Taak Aan
                        </button>
                      )}
                    {selectedTask.assignment_type === "open_to_all" &&
                      selectedTask.claimed_by === user?.id &&
                      selectedTask.status === "in_progress" && (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              completeTask(selectedTask.id);
                              setShowTaskModal(false);
                            }}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            <CheckCircleIcon className="h-4 w-4 mr-1" />
                            Voltooi Taak
                          </button>
                          <button
                            onClick={() => {
                              unclaimTask(selectedTask.id);
                              setShowTaskModal(false);
                            }}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            Laat Los
                          </button>
                        </div>
                      )}
                    {selectedTask.assignment_type === "specific_user" &&
                      selectedTask.status === "pending" &&
                      selectedTask.assigned_to === user?.id && (
                        <button
                          onClick={() => {
                            updateTaskStatus(selectedTask.id, "in_progress");
                            setShowTaskModal(false);
                          }}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          <ClockIcon className="h-4 w-4 mr-1" />
                          Start Taak
                        </button>
                      )}

                    {selectedTask.assignment_type === "specific_user" &&
                      selectedTask.status === "in_progress" &&
                      selectedTask.assigned_to === user?.id && (
                        <button
                          onClick={() => {
                            updateTaskStatus(selectedTask.id, "completed");
                            setShowTaskModal(false);
                          }}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                          Voltooi Taak
                        </button>
                      )}

                    {selectedTask.status === "completed" && (
                      <button
                        onClick={() => {
                          updateTaskStatus(selectedTask.id, "pending");
                          setShowTaskModal(false);
                        }}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-yellow-700 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                      >
                        <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                        Heropen Taak
                      </button>
                    )}

                    <button
                      onClick={() => {
                        deleteTask(selectedTask.id);
                        setShowTaskModal(false);
                      }}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      Verwijder Taak
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-6 pt-4 border-t">
                <button
                  onClick={() => setShowTaskModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Sluiten
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tasks;
